import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
  increment,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import "./PaymentCallBack.css";

const PLAN_RANK = {
  free: 0,
  startup: 1,
  business: 2,
  agency: 3,
};

const PLAN_CREDITS = {
  free: 10,      // (once-off idea, but not applied here unless you choose)
  startup: 70,
  business: 150,
  agency: 300,
};

const safeLower = (v, fallback = "") =>
  String(v ?? fallback).toLowerCase().trim();

const PaymentCallBack = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { currentUser, loading } = useAuth();

  const VERIFY_URL = process.env.REACT_APP_VERIFY_PAYSTACK_URL;

  const [busy, setBusy] = useState(true);
  const [statusMsg, setStatusMsg] = useState("Verifying payment...");
  const [error, setError] = useState("");

  const [planStatus, setPlanStatus] = useState("");
  const [planKey, setPlanKey] = useState("");

  const reference = useMemo(() => {
    return (
      params.get("reference") ||
      params.get("trxref") ||
      localStorage.getItem("kairos_last_paystack_reference") ||
      ""
    );
  }, [params]);

  useEffect(() => {
    if (reference) localStorage.setItem("kairos_last_paystack_reference", reference);
  }, [reference]);

  useEffect(() => {
    // stop AuthContext from bouncing you back to checkout
    localStorage.removeItem("kairos_pending_plan");
    localStorage.removeItem("upgrade_pending_plan");
    localStorage.removeItem("upgrade_pending_billing");
    localStorage.removeItem("kairos_purchase_type");
  }, []);

  const settingsRef = useMemo(() => {
    if (!currentUser) return null;
    return doc(db, "users", currentUser.uid, "settings", "main");
  }, [currentUser]);

  const userRef = useMemo(() => {
    if (!currentUser) return null;
    return doc(db, "users", currentUser.uid);
  }, [currentUser]);

  useEffect(() => {
    const run = async () => {
      if (loading) return;

      if (!currentUser) {
        setBusy(false);
        setError("You are not logged in. Please log in to complete verification.");
        return;
      }

      if (!reference) {
        setBusy(false);
        setError("Missing payment reference in callback URL.");
        return;
      }

      if (!VERIFY_URL) {
        setBusy(false);
        setError("Missing REACT_APP_VERIFY_PAYSTACK_URL in your frontend environment.");
        return;
      }

      if (!settingsRef) {
        setBusy(false);
        setError("Could not load your billing settings. Please refresh and try again.");
        return;
      }

      try {
        setBusy(true);
        setError("");
        setStatusMsg("Checking with Paystack...");

        const settingsSnap = await getDoc(settingsRef);
        const currentSettings = settingsSnap.exists() ? settingsSnap.data() : {};

        const currentPlanKey = safeLower(currentSettings?.planKey, "free");
        const currentPlanStatus = safeLower(currentSettings?.planStatus, "");
        const upgradePlanStatus = safeLower(currentSettings?.upgradePlanStatus, "");
        const isUpgradeFlow = upgradePlanStatus === "upgrading";

        // Verify
        const resp = await fetch(`${VERIFY_URL}?reference=${encodeURIComponent(reference)}`);
        const data = await resp.json();

        if (!resp.ok) throw new Error(data?.error || "Verification failed.");

        const meta = data?.metadata || {};
        const purchaseType = safeLower(meta?.purchaseType, "subscription"); // "subscription" | "credits"

        // ---- Decide target keys based on flow ----
        const metaPlanKey = safeLower(meta?.planKey, "");
        const metaBilling = safeLower(meta?.billing, "monthly");

        const upgradeTargetPlanKey = safeLower(currentSettings?.upgradePlanKey, metaPlanKey || currentPlanKey);
        const upgradeTargetBilling = safeLower(currentSettings?.upgradeBillingCycle, metaBilling);

        const newPurchasePlanKey = metaPlanKey || safeLower(currentSettings?.planKey, "");
        const newPurchaseBilling = metaBilling;

        // which flow is this?
        const isNewPurchaseFlow = !isUpgradeFlow && currentPlanStatus === "payment-ongoing";

        // credits purchase (doesn’t depend on planStatus flags)
        const isCreditsFlow = purchaseType === "credits";

        // store for debug/UI
        setPlanKey(isUpgradeFlow ? upgradeTargetPlanKey : newPurchasePlanKey);

        // ---- FAIL ----
        if (data.status !== "success") {
          setStatusMsg("Payment failed.");
          setPlanStatus("failed");

          const failedBase = {
            paystackReference: data.reference || reference,
            paystackPaidAt: data.paidAt || null,
            paystackCurrency: data.currency || "ZAR",
            lastPaymentReference: data.reference || reference,
            updatedAt: serverTimestamp(),
          };

          if (isCreditsFlow) {
            await updateDoc(settingsRef, {
              ...failedBase,
              creditPurchaseStatus: "failed",
              creditPurchaseLastReference: data.reference || reference,
            });

            navigate(`/credits-purchase-outcome?status=failed&ref=${encodeURIComponent(data.reference || reference)}`, {
              replace: true,
            });
            return;
          }

          if (isUpgradeFlow) {
            await updateDoc(settingsRef, {
              ...failedBase,
              upgradePlanStatus: "failed",
              upgradeLastPaymentReference: data.reference || reference,
            });
            navigate('/upgrade-payment-outcome', {replace: true});
          } else if (isNewPurchaseFlow) {
            await updateDoc(settingsRef, {
              ...failedBase,
              planStatus: "failed",
            });
          } else {
            await updateDoc(settingsRef, {
              ...failedBase,
              planStatusSetBy: "PaymentCallBack: failed (no matching flow)",
            });
          }
        }

        // ---- SUCCESS ----
        setStatusMsg("Payment verified successfully.");
        setPlanStatus("success");

        // Common captured auth details
        const successBase = {
          paystackReference: data.reference || reference,
          paystackPaidAt: data.paidAt || null,
          paystackCurrency: data.currency || "ZAR",
          lastPaymentReference: data.reference || reference,

          paystackAuthorizationCode: data.authorization?.authorization_code || null,
          paystackCustomerCode: data.customer?.customer_code || null,
          paystackAuthorizationReusable: data.authorization?.reusable ?? null,
          paystackAuthorizationLast4: data.authorization?.last4 ?? null,
          paystackAuthorizationExpMonth: data.authorization?.exp_month ?? null,
          paystackAuthorizationExpYear: data.authorization?.exp_year ?? null,

          amountUsd: Number(meta?.amountUsd || 0),
          billingEmail: currentUser.email,

          updatedAt: serverTimestamp(),
        };

        // ✅ 4) Prepaid credits: add credits only, then route to Credits outcome page
        if (isCreditsFlow) {
          const creditsQty = Number(meta?.creditsQty || 0);
          if (!creditsQty || creditsQty <= 0) {
            throw new Error("Credits purchase verified but creditsQty is missing in metadata.");
          }

          // settings: record credit purchase result
          await updateDoc(settingsRef, {
            ...successBase,
            creditPurchaseStatus: "success",
            creditPurchaseCredits: creditsQty,
            creditPurchaseTokens: Number(meta?.tokens || creditsQty * 1000),
            creditPurchaseLastReference: data.reference || reference,
          });

          // users doc: increment credits balance
          if (userRef) {
            await updateDoc(userRef, {
              credits: increment(creditsQty),
              updatedAt: serverTimestamp(),
            });
          }

          console.log(planStatus);
          console.log(planKey);
          navigate(
            `/credits-purchase-outcome?status=success&credits=${creditsQty}&ref=${encodeURIComponent(
              data.reference || reference
            )}`,
            { replace: true }
          );
          return;
        }

        // for subscription flows we compute renewal based on relevant billing cycle
        const billingCycle = isUpgradeFlow ? upgradeTargetBilling : newPurchaseBilling;

        // ✅ Helper: convert Firestore Timestamp/string/Date -> Date (or null)
        // ✅ Helper: add one billing period
        const toDateSafe = (v) => {
          if (!v) return null;
          if (v instanceof Date) return v;
          if (typeof v === "string") {
            const d = new Date(v);
            return isNaN(d.getTime()) ? null : d;
          }
          if (typeof v === "object" && typeof v.toDate === "function") return v.toDate(); // Firestore Timestamp
          return null;
        };
        
        const addOnePeriod = (baseDate, cycle) => {
          const d = new Date(baseDate);
          if (cycle === "annual") d.setFullYear(d.getFullYear() + 1);
          else d.setMonth(d.getMonth() + 1); // monthly default
          return d;
        };
        
        // ✅ REQUIREMENT: always use CURRENT plan's renewalAt as the base for upgrade renewals
        const now = new Date();
        const currentRenewalAt = toDateSafe(currentSettings?.renewalAt);
        
        // If renewalAt is missing, use now. If it's in the past, also use now.
        const baseForUpgrade = (currentRenewalAt && currentRenewalAt > now) ? currentRenewalAt : now;
        
        // ✅ NEW plan renewal = (current plan end date) + (new plan billing cycle)
        const next = addOnePeriod(baseForUpgrade, billingCycle);

        // ✅ 2/3) Upgrade/Downgrade flow
        if (isUpgradeFlow) {
          const targetPlanKey = upgradeTargetPlanKey || currentPlanKey;
          const currentRank = PLAN_RANK[currentPlanKey] ?? 0;
          const targetRank = PLAN_RANK[targetPlanKey] ?? currentRank;

          const isUpgrade = targetRank > currentRank;
          const isDowngrade = targetRank < currentRank;

          // always mark upgrade flow success and store reference
          const upgradeSuccess = {
            ...successBase,
            upgradePlan: "paid",
            upgradePlanStatus: "upgraded",
            upgradeLastPaymentReference: data.reference || reference,
            upgradePlanKey: targetPlanKey,
            upgradeBillingCycle: billingCycle,

            // requested: next billing date should follow the changed plan + billing cycle
            renewalAt: Timestamp.fromDate(next),
            currentPeriodStart: serverTimestamp(),
          };

          // If it is an UPGRADE: apply immediately + add plan credits
          if (isUpgrade) {
            await updateDoc(settingsRef, {
              ...upgradeSuccess,

              // apply new plan immediately
              plan: "paid",
              planKey: targetPlanKey,
              planStatus: "active",
              billingCycle: billingCycle,
              upgradePlanKey: targetPlanKey,
              upgradePlanStatus: "upgraded",
            });

            // add/increment user credits by the upgraded plan credits
            const addCredits = PLAN_CREDITS[targetPlanKey] ?? 0;
            console.log(addCredits);
            if (addCredits > 0 && userRef) {
              await updateDoc(userRef, {
                credits: increment(addCredits),
                updatedAt: serverTimestamp(),
              });
            }

            console.log(planStatus);
            console.log(planKey);
          }

          // If it is a DOWNGRADE: keep current plan/credits, but schedule the downgrade info
          else if (isDowngrade) {
            await updateDoc(settingsRef, {
              ...upgradeSuccess,

              // keep current plan untouched
              // schedule the downgrade for later processing (e.g. on renewal)
              scheduledPlanChangeType: "downgrade",
              scheduledPlanKey: targetPlanKey,
              scheduledBillingCycle: billingCycle,
              scheduledAmoundUsd: Number(meta?.amountUsd || 0),
              scheduledEffectiveAt: Timestamp.fromDate(next),
              upgradePlan: "paid",
              upgradePlanKey: targetPlanKey,
              upgradePlanStatus: "downgraded",
            });

            console.log(planStatus);
            console.log(planKey);
          }

          // Same plan rank (rare): treat as “change billing cycle” or “same-tier change”
          else {
            await updateDoc(settingsRef, {
              ...upgradeSuccess,
              scheduledPlanChangeType: "lateral",
              scheduledPlanKey: targetPlanKey,
              scheduledBillingCycle: billingCycle,
              scheduledEffectiveAt: Timestamp.fromDate(next),

              // apply new plan immediately
              plan: "paid",
              planKey: targetPlanKey,
              planStatus: "active",
              billingCycle: billingCycle,
            });
          }
          console.log(planStatus);
          console.log(planKey);
          return;
        }

        // ✅ 1) New purchase flow: NO CHANGE (keep your behavior: planStatus = "success", not active yet)
        if (isNewPurchaseFlow) {
          const verifiedPlanKey = newPurchasePlanKey || currentPlanKey;

          await updateDoc(settingsRef, {
            ...successBase,
            planStatus: "success",
            plan: "paid",
            planKey: verifiedPlanKey,

            renewalAt: Timestamp.fromDate(next),
            currentPeriodStart: serverTimestamp(),
            billingCycle: billingCycle,
          });

          const planCredits = PLAN_CREDITS[verifiedPlanKey] ?? 0;
          if (userRef && planCredits > 0) {
            await updateDoc(userRef, {
              credits: increment(planCredits),
              updatedAt: serverTimestamp(),
            })
          }
        }

        // fallback: don’t overwrite anything dangerous
        await updateDoc(settingsRef, {
          ...successBase,
          planStatusSetBy: "PaymentCallBack: success (no matching flow)",
        });
        console.log(planStatus);
        console.log(planKey);
      } catch (e) {
        const msg = e?.message || "Something went wrong.";
        setError(msg);
        setPlanStatus("failed");

        // minimal safe record (don’t overwrite main plan accidentally)
        try {
          if (settingsRef) {
            await updateDoc(settingsRef, {
              planStatusSetBy: "PaymentCallBack: catch",
              updatedAt: serverTimestamp(),
            });
          }
        } catch (_) {}

        // if we error during credits flow, still allow user to see something
        navigate(`/credits-purchase-outcome?status=failed&ref=${encodeURIComponent(reference || "")}`, {
          replace: true,
        });
      } finally {
        setBusy(false);
      }
    };

    run();
  }, [currentUser, loading, reference, VERIFY_URL, settingsRef, userRef, planKey, planStatus, navigate]);

  return (
    <main className="home">
      <section className="checkout payment-callback">
        <div className="checkout-header">
          <h2>
            Processing <span className="gradient-text">Payment</span>
          </h2>
          <p className="checkout-subtitle">
            We’re finalising your payment details. Please wait while we redirect you.
          </p>
        </div>

        <div className="checkout-grid payment-grid">
          <div className="checkout-card featured payment-card redirect-card">
            <div className="redirect-loader">
              <div className="spinner" />
              <p className="redirect-title">{busy ? "Redirecting..." : "Done"}</p>
              <p className="redirect-text">{error ? error : statusMsg}</p>
            </div>

            <div className="redirect-actions">
              <button
                className="primary-btn"
                onClick={() => navigate("/payment-outcome", { replace: true })}
              >
                Continue manually
              </button>

              <button
                className="secondary-btn"
                onClick={() => navigate("/pricing", { replace: true })}
              >
                Back to pricing
              </button>
            </div>

            <p className="tiny redirect-footnote">
              If nothing happens automatically, click “Continue manually”.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PaymentCallBack;
